import { ReactNode } from 'react';
import {
  Dialog,
  Modal,
  ModalOverlay,
  Heading,
} from 'react-aria-components';
import { X } from 'lucide-react';
import { cx } from './cx';
import { Icon } from './Icon';
import { IconButton } from './IconButton';
import { useT } from '../../modules/i18n';

export interface DrawerProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Which edge the panel slides in from. Default 'left'. */
  side?: 'left' | 'right';
  children: ReactNode;
}

/**
 * A slide-in overlay panel anchored to a screen edge (default left). Built on
 * react-aria-components so focus-trapping, Esc-to-close and overlay-click
 * dismissal come for free. Large but responsive: min(90vw, 640px).
 */
export const Drawer = ({
  isOpen,
  onOpenChange,
  title,
  side = 'left',
  children,
}: DrawerProps) => {
  const t = useT();
  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isDismissable
      className={cx(
        'fixed inset-0 z-50 flex bg-overlay backdrop-blur-[1px]',
        side === 'left' ? 'justify-start' : 'justify-end',
      )}
    >
      <Modal
        className={cx(
          'h-full w-[min(90vw,640px)] bg-surface shadow-xl outline-none',
          side === 'left' ? 'border-r border-default' : 'border-l border-default',
        )}
      >
        <Dialog aria-label={title} className="flex h-full flex-col outline-none">
          <div className="flex items-center justify-between border-b border-default px-5 py-4">
            <Heading slot="title" className="text-lg font-semibold text-fg">
              {title}
            </Heading>
            <IconButton
              aria-label={t('ui.drawer.closeAriaLabel')}
              size="sm"
              onPress={() => onOpenChange(false)}
            >
              <Icon as={X} size="sm" />
            </IconButton>
          </div>
          <div className="flex-1 overflow-y-auto p-5">{children}</div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
};
